import { DatePipe } from '@angular/common';
import { Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { FiltroPadraoService } from '../../../services/filtro-padrao.service';
import {
  CHAVE_CAMPO_CLIENTE,
  CHAVE_CAMPO_UNIDADE,
  DEFAULT_OPERADOR_LOGICO_AND_ID,
  DEFAULT_OPERADOR_LOGICO_OR_ID,
  fromApiToForm,
  hasEmptyGroups,
  MAX_GRUPOS_CONDICOES,
  OPERADOR_ENTRE_ID,
  TEXTO_TIPO_OPERADOR,
  toPayload
} from '../helpers/filtro-avancado-v2.helper';
import { FiltroAvancadoV2FormValue, FiltroAvancadoV2Payload } from '../models/filtro-avancado-v2.model';
import { FieldTypeEnum } from '../../../enums/fieldType.enum';
import { ModalPeriodoFiltroAvancadoV2Component } from './modal-periodo-filtro-avancado-v2/modal-periodo-filtro-avancado-v2.component';
import { ModalCondicaoFiltroAvancadoV2Component } from './modal-condicao-filtro-avancado-v2/modal-condicao-filtro-avancado-v2.component';

@Component({
  selector: 'app-modal-filtro-avancado-v2',
  templateUrl: './modal-filtro-avancado-v2.component.html',
  styleUrls: ['./modal-filtro-avancado-v2.component.scss']
})
export class ModalFiltroAvancadoV2Component implements OnInit, OnDestroy {
  @Input() filtro_id: number;
  // Tela que define o contexto do filtro quando a URL tem parâmetro (ex.: /tickets/kanban/:id).
  @Input() telaContexto: string;

  filtroForm: UntypedFormGroup;
  create = true;
  operadorLogicoPeriodos = DEFAULT_OPERADOR_LOGICO_AND_ID;
  readonly maxGruposCondicoes = MAX_GRUPOS_CONDICOES;
  readonly fieldTypeEnum = FieldTypeEnum;

  private operadores: any[] = [];
  private selector: string;
  private newSubs: Subscription;
  private readonly datePipe = new DatePipe('en-US');
  private datasDinamicas: any[] = [];
  private valoresCondicaoOptions: { [chave: string]: any[] } = {};

  constructor(
    public modal: NgbActiveModal,
    private elemRef: ElementRef,
    public translate: TranslateService,
    private filtrosService: FiltroPadraoService,
    private toastr: ToastrService,
    private fb: UntypedFormBuilder,
    private router: Router,
    private modalService: NgbModal
  ) {
    this.selector = this.elemRef.nativeElement.tagName.toLowerCase();
  }

  ngOnInit(): void {
    document.getElementsByTagName(this.selector)[0]['style']['zoom'] = '0.85';
    this.buildForm();
    this.carregaDatasDinamicas();
    this.carregaOperadores();

    if (this.filtro_id) {
      this.create = false;
      this.carregaFiltro();
    }
  }

  carregaDatasDinamicas(): void {
    this.filtrosService.getDropdownValores('$datas').subscribe(
      (results) => this.datasDinamicas = results || [],
      (error) => console.error('carregaDatasDinamicas', error)
    );
  }

  // Todos os operadores (de todos os tipos de campo), para exibir o texto pelo id salvo na condição.
  carregaOperadores(): void {
    this.filtrosService.getDropdownOperadores({}).subscribe(
      (results) => this.operadores = results || [],
      () => this.operadores = []
    );
  }

  private carregaValoresCondicao(chave: string, valor?: any): void {
    if (chave === CHAVE_CAMPO_UNIDADE) {
      this.carregaUnidadesCondicao(valor);
      return;
    }

    if (!chave || this.valoresCondicaoOptions[chave]) {
      return;
    }

    this.valoresCondicaoOptions[chave] = [];
    this.filtrosService.getDropdownValores(chave).subscribe(
      (results) => this.valoresCondicaoOptions[chave] = results || [],
      (error) => console.error('carregaValoresCondicao', error)
    );
  }

  // Unidade depende do cliente, então o nome é buscado pelos ids salvos na condição.
  private carregaUnidadesCondicao(valor: any): void {
    const carregadas = this.valoresCondicaoOptions[CHAVE_CAMPO_UNIDADE] || [];
    const ids = (Array.isArray(valor) ? valor : [valor])
      .filter((id) => id !== null && id !== undefined && !carregadas.some((opcao) => String(opcao.id) === String(id)));
    if (!ids.length) {
      return;
    }

    this.filtrosService.getDropdownValores(CHAVE_CAMPO_UNIDADE, { id: ids.join(';') }).subscribe(
      (results) => this.valoresCondicaoOptions[CHAVE_CAMPO_UNIDADE] = [...(this.valoresCondicaoOptions[CHAVE_CAMPO_UNIDADE] || []), ...(results || [])],
      (error) => console.error('carregaUnidadesCondicao', error)
    );
  }

  ngOnDestroy(): void {
    this.newSubs?.unsubscribe();
  }

  get periodos(): UntypedFormArray {
    return this.filtroForm.get('periodos') as UntypedFormArray;
  }

  get condicoes(): UntypedFormArray {
    return this.filtroForm.get('condicoes') as UntypedFormArray;
  }

  get grupos(): UntypedFormArray {
    return this.filtroForm.get('grupos') as UntypedFormArray;
  }

  buildForm(): void {
    this.filtroForm = this.fb.group({
      id: [null],
      nome: ['', Validators.required],
      tela: [this.router.url],
      visibilidade_id: [null, Validators.required],
      periodos: this.fb.array([]),
      condicoes: this.fb.array([]),
      grupos: this.fb.array([])
    });
  }

  carregaFiltro(): void {
    // A lista de campos dá o nome e o tipo das condições de campo personalizado (MIL-30113).
    const campos$ = this.filtrosService.getDropdownCampos().pipe(catchError(() => of([])));
    this.newSubs = forkJoin([this.filtrosService.getFiltro(this.filtro_id), campos$]).subscribe(
      ([filtro, campos]) => this.patchForm(fromApiToForm(filtro, this.router.url, campos)),
      (error) => console.error('carregaFiltroAvancadoV2', error)
    );
  }

  patchForm(formValue: FiltroAvancadoV2FormValue): void {
    this.filtroForm.patchValue({
      id: formValue.id,
      nome: formValue.nome,
      tela: formValue.tela || this.router.url,
      visibilidade_id: formValue.visibilidade_id
    });
    formValue.periodos.forEach((periodo) => this.periodos.push(this.criaPeriodoGroup(periodo)));
    formValue.condicoes.forEach((condicao) => this.condicoes.push(this.criaCondicaoGroup(condicao)));
    formValue.grupos.forEach((grupo) => this.grupos.push(this.criaGrupoGroup(grupo)));

    const todasCondicoes = [...formValue.condicoes, ...formValue.grupos.flatMap((grupo) => grupo.condicoes)];
    todasCondicoes.forEach((condicao) => {
      const tipoCampoId = Number(condicao.tipo_campo_id);
      if (condicao.campo_chave && (tipoCampoId === FieldTypeEnum.ListaEnum || tipoCampoId === FieldTypeEnum.ListaRelacionamento)) {
        this.carregaValoresCondicao(condicao.campo_chave, condicao.valor);
      }
    });

    const primeiroPeriodoComOperador = formValue.periodos.find((periodo) => !!periodo.operador_logico_id);
    if (primeiroPeriodoComOperador) {
      this.operadorLogicoPeriodos = primeiroPeriodoComOperador.operador_logico_id;
    }
  }

  openPeriodo(index: number | null = null): void {
    const periodo = index !== null ? this.periodos.at(index).value : null;
    const mostrarOperadorLogico = index !== null ? index === 1 : this.periodos.length === 1;

    const modalRef = this.modalService.open(ModalPeriodoFiltroAvancadoV2Component, {
      backdrop: 'static',
      keyboard: false,
      scrollable: false,
      size: 'lg',
      windowClass: 'modal-custom-milvus'
    });

    modalRef.componentInstance.periodo = periodo;
    modalRef.componentInstance.mostrarOperadorLogico = mostrarOperadorLogico;
    modalRef.componentInstance.operadorLogicoAtual = this.operadorLogicoPeriodos;

    modalRef.result.then((resultado) => {
      if (!resultado) {
        return;
      }

      if (mostrarOperadorLogico && resultado.operador_logico_id) {
        this.operadorLogicoPeriodos = resultado.operador_logico_id;
      }

      if (index !== null) {
        this.periodos.at(index).patchValue(resultado);
      } else {
        this.periodos.push(this.criaPeriodoGroup(resultado));
      }

      this.reaplicaOperadorPeriodos();
      this.filtroForm.markAsTouched();
    }, () => undefined);
  }

  removePeriodo(index: number): void {
    this.confirm(this.translate.instant('DESEJA_DELETAR_PERIODO')).then((result) => {
      if (result.value || result.isConfirmed) {
        this.periodos.removeAt(index);
        this.reaplicaOperadorPeriodos();
      }
    });
  }

  openCondicao(grupoIndex: number | null = null, index: number | null = null): void {
    const lista = grupoIndex === null ? this.condicoes : this.getCondicoesGrupo(grupoIndex);
    const condicao = index !== null ? lista.at(index).value : null;

    const modalRef = this.modalService.open(ModalCondicaoFiltroAvancadoV2Component, {
      backdrop: 'static',
      keyboard: false,
      scrollable: false,
      size: 'lg',
      windowClass: 'modal-custom-milvus'
    });

    modalRef.componentInstance.condicao = condicao;
    modalRef.componentInstance.clientesSelecionados = this.getClientesSelecionados(lista);

    modalRef.result.then((resultado) => {
      if (!resultado) {
        return;
      }

      const tipoCampoId = Number(resultado.tipo_campo_id);
      if (resultado.campo_chave && (tipoCampoId === FieldTypeEnum.ListaEnum || tipoCampoId === FieldTypeEnum.ListaRelacionamento)) {
        this.carregaValoresCondicao(resultado.campo_chave, resultado.valor);
      }

      if (index !== null) {
        resultado.operador_logico_id = lista.at(index).value.operador_logico_id;
        lista.at(index).patchValue(resultado);
      } else {
        resultado.operador_logico_id = lista.length === 0 ? null : DEFAULT_OPERADOR_LOGICO_AND_ID;
        lista.push(this.criaCondicaoGroup(resultado));
      }

      this.filtroForm.markAsTouched();
    }, () => undefined);
  }

  removeCondicao(index: number, grupoIndex?: number): void {
    this.confirm(this.translate.instant('DESEJA_DELETAR_CONDICAO')).then((result) => {
      if (result.value || result.isConfirmed) {
        const isGrupo = grupoIndex !== undefined;
        const control = isGrupo ? this.getCondicoesGrupo(grupoIndex) : this.condicoes;
        control.removeAt(index);
        this.reaplicaOperadorCondicoes(control);
      }
    });
  }

  novoGrupo(): void {
    if (this.grupos.length < MAX_GRUPOS_CONDICOES) {
      this.grupos.push(this.criaGrupoGroup({ operador_logico_id: DEFAULT_OPERADOR_LOGICO_OR_ID, condicoes: [] }));
    }
  }

  removeGrupo(index: number): void {
    this.confirm(this.translate.instant('DESEJA_DELETAR_GRUPO_CONDICOES')).then((result) => {
      if (result.value || result.isConfirmed) {
        this.grupos.removeAt(index);
      }
    });
  }

  salvaFiltro(): void {
    const payload = toPayload(this.filtroForm.value as FiltroAvancadoV2FormValue);
    payload.tela = this.telaContexto || this.router.url;

    if (!this.canSave()) {
      this.filtroForm.markAllAsTouched();
      if (hasEmptyGroups(payload.grupos)) {
        this.toastr.show(this.translate.instant('GRUPO_CONDICOES_VAZIO'), '', environment.toastrConfig('warning'));
      }
      return;
    }

    if (this.create) {
      this.criaFiltro(payload);
    } else {
      this.atualizaFiltro(payload);
    }
  }

  criaFiltro(payload: FiltroAvancadoV2Payload): void {
    this.newSubs = this.filtrosService.criarFiltro(payload).subscribe(
      (results) => {
        this.toastr.show(this.translate.instant('SALVO_SUCESSO'), '', environment.toastrConfig('success'));
        this.modal.close(results);
      },
      (error) => console.error('criaFiltroAvancadoV2', error)
    );
  }

  atualizaFiltro(payload: FiltroAvancadoV2Payload): void {
    this.newSubs = this.filtrosService.updateFiltro(this.filtro_id, payload).subscribe(
      (results) => {
        this.toastr.show(this.translate.instant('SALVO_SUCESSO'), '', environment.toastrConfig('success'));
        this.modal.close(results);
      },
      (error) => console.error('atualizaFiltroAvancadoV2', error)
    );
  }

  close(): void {
    if (this.filtroForm?.touched) {
      const configAlert = {
        title: this.translate.instant('HA_ALTERACOES_NAO_SALVAS'),
        text: this.translate.instant('PROSSEGUIR_MESMO_ASSIM?'),
        icon: 'warning',
        confirmButtonText: this.translate.instant('SIM'),
        cancelButtonText: this.translate.instant('NAO')
      };

      environment.disparaAlerta(configAlert).then((result) => {
        if (result.value || result.isConfirmed) {
          this.modal.close();
        }
      });
    } else {
      this.modal.close();
    }
  }

  getCondicoesGrupo(index: number): UntypedFormArray {
    return this.grupos.at(index).get('condicoes') as UntypedFormArray;
  }

  // O filtro precisa de ao menos um período ou uma condição (da lista E ou de um grupo OU).
  canSave(): boolean {
    const payload = toPayload(this.filtroForm.value as FiltroAvancadoV2FormValue);
    const temCriterio = payload.periodos.length > 0 || payload.condicoes.length > 0 || payload.grupos.length > 0;
    return this.filtroForm?.valid && temCriterio && !hasEmptyGroups(payload.grupos);
  }

  trackByIndex(index): any {
    return index;
  }

  getOperadorPeriodoText(operadorId: number): string {
    return this.getOperadorText(operadorId);
  }

  getOperadorCondicaoText(operadorId: number): string {
    return this.getOperadorText(operadorId);
  }

  private getOperadorText(operadorId: number): string {
    const operador = this.operadores.find((item) => Number(item.id) === Number(operadorId));
    return operador ? (TEXTO_TIPO_OPERADOR[operador.tipo_operador_id] || operador.text) : `#${operadorId}`;
  }

  private getTipoOperador(operadorId: number): number {
    const operador = this.operadores.find((item) => Number(item.id) === Number(operadorId));
    return operador ? Number(operador.tipo_operador_id) : null;
  }

  // Clientes escolhidos (operador Igual) nas condições de Cliente da mesma lista.
  private getClientesSelecionados(lista: UntypedFormArray): Array<any> {
    return (lista.value || [])
      .filter((condicao) => condicao.campo_chave === CHAVE_CAMPO_CLIENTE && this.getTipoOperador(condicao.operador_id) === 1)
      .reduce((clientes, condicao) => clientes.concat(Array.isArray(condicao.valor) ? condicao.valor : [condicao.valor]), [])
      .filter((cliente) => cliente !== null && cliente !== undefined);
  }

  formatValorPeriodo(periodo: any): string {
    if (periodo.dinamico) {
      return this.getValorDinamicoText(periodo.valor);
    }

    if (this.getTipoOperador(periodo.operador_id) === OPERADOR_ENTRE_ID) {
      return `${this.formatData(periodo.valor_inicio)} - ${this.formatData(periodo.valor_fim)}`;
    }

    return Number(periodo.tipo_campo_id) === 3 ? this.formatData(periodo.valor) : periodo.valor;
  }

  private getValorDinamicoText(chave: string): string {
    const item = this.datasDinamicas.find((data) => data.chave === chave);
    return item ? (item.text || item.label || chave) : chave;
  }

  formatValorCondicao(condicao: any): string {
    if (Number(condicao.tipo_campo_id) === 3) {
      return this.formatData(condicao.valor);
    }

    if (Number(condicao.tipo_campo_id) === 5) {
      return condicao.valor === true || condicao.valor === 'true' ? this.translate.instant('SIM') : this.translate.instant('NAO');
    }

    if (Number(condicao.tipo_campo_id) === FieldTypeEnum.ListaEnum) {
      return this.getValorListaText(condicao.campo_chave, condicao.valor);
    }

    if (Number(condicao.tipo_campo_id) === FieldTypeEnum.ListaRelacionamento) {
      const valores = Array.isArray(condicao.valor) ? condicao.valor : [condicao.valor];
      return valores.map((valor: any) => this.getValorListaText(condicao.campo_chave, valor)).join(', ');
    }

    return condicao.valor;
  }

  private getValorListaText(chave: string, valor: any): string {
    const opcoes = this.valoresCondicaoOptions[chave] || [];
    const item = opcoes.find((opcao) => String(opcao.id) === String(valor));
    return item ? (item.text || item.label || item.nome || item.descricao || valor) : valor;
  }

  private formatData(valor: any): string {
    if (!valor) {
      return '';
    }

    // Valor que não é data (ex.: chave de data dinâmica) é exibido como veio, sem quebrar o modal.
    try {
      return this.datePipe.transform(valor, 'dd/MM/yyyy') || valor;
    } catch {
      return this.getValorDinamicoText(valor);
    }
  }

  private criaPeriodoGroup(periodo: any): UntypedFormGroup {
    const valorEntre = Array.isArray(periodo.valor) ? periodo.valor : [];
    return this.fb.group({
      campo_id: [periodo.campo_id, Validators.required],
      campo_text: [periodo.campo_text],
      operador_id: [periodo.operador_id, Validators.required],
      dinamico: [periodo.dinamico === true],
      valor: [periodo.valor, Validators.required],
      valor_inicio: [periodo.valor_inicio || valorEntre[0] || null],
      valor_fim: [periodo.valor_fim || valorEntre[1] || null],
      entidade_id: [periodo.entidade_id],
      tipo_campo_id: [periodo.tipo_campo_id || 3],
      campo_chave: [periodo.campo_chave],
      operador_logico_id: [periodo.operador_logico_id]
    });
  }

  private criaCondicaoGroup(condicao: any): UntypedFormGroup {
    return this.fb.group({
      entidade_id: [condicao.entidade_id],
      campo_id: [condicao.campo_id, Validators.required],
      campo_text: [condicao.campo_text],
      operador_id: [condicao.operador_id, Validators.required],
      valor: [condicao.valor, Validators.required],
      tipo_campo_id: [condicao.tipo_campo_id],
      campo_chave: [condicao.campo_chave],
      campo_personalizado_id: [condicao.campo_personalizado_id || null],
      operador_logico_id: [condicao.operador_logico_id || null]
    });
  }

  private criaGrupoGroup(grupo: any): UntypedFormGroup {
    return this.fb.group({
      operador_logico_id: [DEFAULT_OPERADOR_LOGICO_OR_ID],
      condicoes: this.fb.array((grupo.condicoes || []).map((condicao) => this.criaCondicaoGroup(condicao)))
    });
  }

  private reaplicaOperadorPeriodos(): void {
    this.periodos.controls.forEach((periodo, index) => {
      periodo.patchValue({ operador_logico_id: index === 0 ? null : this.operadorLogicoPeriodos });
    });
  }

  private reaplicaOperadorCondicoes(lista: UntypedFormArray): void {
    lista.controls.forEach((condicao, index) => {
      condicao.patchValue({ operador_logico_id: index === 0 ? null : DEFAULT_OPERADOR_LOGICO_AND_ID });
    });
  }

  private confirm(title: string): Promise<any> {
    const configAlerta = {
      title,
      text: null,
      icon: 'error',
      confirmButtonText: this.translate.instant('SIM'),
      cancelButtonText: this.translate.instant('NAO')
    };

    return environment.disparaAlerta(configAlerta);
  }
}
